import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaPhone, FaEnvelope, FaUserCircle, FaChevronDown, FaSun, FaMoon, FaBars, FaTimes } from 'react-icons/fa';
import { getCurrentUser, logout } from '../../../services/authService';
import publicData from '../../../data/publicData.json';

const TopNavbar = ({ theme, onToggleTheme }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getCurrentUser());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { company } = publicData;
  const { contactInfo, socialLinks } = company;
  
  const bgClass = theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100';
  const textClass = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const hoverClass = theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900';
  const dropdownBgClass = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const mobileBgClass = theme === 'dark' ? 'bg-gray-900' : 'bg-white';

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  const handleLogout = () => {
    logout();
    setUser(null);
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.user-menu')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const renderUserMenu = () => (
    <div className="flex flex-col space-y-4">
      <div className="flex items-center space-x-2">
        <FaUserCircle size={20} />
        <span className="font-medium">{user?.firstName || 'Guest'}</span>
      </div>
      {user ? (
        <>
          <Link
            to="/my-profile"
            className={`block py-2 ${textClass} ${hoverClass}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            My Profile
          </Link>
          <Link
            to="/my-settings"
            className={`block py-2 ${textClass} ${hoverClass}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Settings
          </Link>
          {user.role === 'admin' && (
            <Link
              to="/admin/dashboard"
              className={`block py-2 ${textClass} ${hoverClass}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Admin Dashboard
            </Link>
          )}
          <button
            onClick={handleLogout}
            className={`block w-full text-left py-2 ${textClass} ${hoverClass}`}
          >
            Logout
          </button>
        </>
      ) : (
        <div className="flex flex-col space-y-4">
          <Link
            to="/login"
            className={`block py-2 ${textClass} ${hoverClass} font-medium`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Login
          </Link>
          <Link
            to="/register"
            className={`block py-2 ${textClass} ${hoverClass} font-medium`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Sign Up
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <div className={`${bgClass} py-2`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Left side - Contact Info */}
          <div className="hidden sm:flex items-center space-x-4">
            {contactInfo?.phone && (
              <a 
                href={`tel:${contactInfo.phone}`} 
                className={`${textClass} ${hoverClass} flex items-center text-sm`}
              >
                <FaPhone className="mr-2" size={12} />
                <span>{contactInfo.phone}</span>
              </a>
            )}
            {contactInfo?.email && (
              <a 
                href={`mailto:${contactInfo.email}`} 
                className={`${textClass} ${hoverClass} flex items-center text-sm`}
              >
                <FaEnvelope className="mr-2" size={12} />
                <span>{contactInfo.email}</span>
              </a>
            )}
          </div>

          {/* Right side - Social Links, Auth, and Theme Toggle */}
          <div className="flex items-center space-x-6 ml-auto">
            {/* Social Links */}
            <div className="hidden sm:flex items-center space-x-3">
              {socialLinks?.facebook && (
                <a 
                  href={socialLinks.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`${textClass} ${hoverClass}`}
                >
                  <FaFacebook size={14} />
                </a>
              )}
              {socialLinks?.twitter && (
                <a 
                  href={socialLinks.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`${textClass} ${hoverClass}`}
                >
                  <FaTwitter size={14} />
                </a>
              )}
              {socialLinks?.linkedin && (
                <a 
                  href={socialLinks.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`${textClass} ${hoverClass}`}
                >
                  <FaLinkedin size={14} />
                </a>
              )}
              {socialLinks?.instagram && (
                <a 
                  href={socialLinks.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`${textClass} ${hoverClass}`}
                >
                  <FaInstagram size={14} />
                </a>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={onToggleTheme}
              className={`p-1.5 rounded-full focus:outline-none transition-colors duration-200 ${
                theme === 'dark' 
                  ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <FaSun size={14} /> : <FaMoon size={14} />}
            </button>

            {/* Mobile Menu Button */}
            <button
              className="sm:hidden p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <FaTimes size={20} className={textClass} />
              ) : (
                <FaUserCircle size={20} className={textClass} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`fixed inset-0 z-50 sm:hidden ${mobileBgClass}`}>
            <div className="container mx-auto px-4 py-6">
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 ${textClass}`}
                >
                  <FaTimes size={24} />
                </button>
              </div>
              <div className="flex flex-col space-y-6">
                {/* Mobile Contact Info */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className={`text-sm font-semibold mb-4 ${textClass}`}>Contact Info</h3>
                  <div className="space-y-4">
                    {contactInfo?.phone && (
                      <a 
                        href={`tel:${contactInfo.phone}`} 
                        className={`${textClass} ${hoverClass} flex items-center`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FaPhone className="mr-3" size={16} />
                        <span>{contactInfo.phone}</span>
                      </a>
                    )}
                    {contactInfo?.email && (
                      <a 
                        href={`mailto:${contactInfo.email}`} 
                        className={`${textClass} ${hoverClass} flex items-center`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FaEnvelope className="mr-3" size={16} />
                        <span>{contactInfo.email}</span>
                      </a>
                    )}
                  </div>
                </div>

                {/* Mobile Social Links */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className={`text-sm font-semibold mb-4 ${textClass}`}>Follow Us</h3>
                  <div className="flex space-x-6">
                    {socialLinks?.facebook && (
                      <a 
                        href={socialLinks.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`${textClass} ${hoverClass}`}
                      >
                        <FaFacebook size={20} />
                      </a>
                    )}
                    {socialLinks?.twitter && (
                      <a 
                        href={socialLinks.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`${textClass} ${hoverClass}`}
                      >
                        <FaTwitter size={20} />
                      </a>
                    )}
                    {socialLinks?.linkedin && (
                      <a 
                        href={socialLinks.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`${textClass} ${hoverClass}`}
                      >
                        <FaLinkedin size={20} />
                      </a>
                    )}
                    {socialLinks?.instagram && (
                      <a 
                        href={socialLinks.instagram} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={`${textClass} ${hoverClass}`}
                      >
                        <FaInstagram size={20} />
                      </a>
                    )}
                  </div>
                </div>

                {/* Mobile User Menu */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  {renderUserMenu()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopNavbar; 